import { Project } from '@brg/api';
import {Colors, FInter, FlexBox, Skeleton, useBreakpoint} from '@brg/ui';
import { LayoutConst } from 'core';
import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useRouteMatch } from 'react-router-dom';

import {ApplicationTheme, ApplicationThemeT} from 'shared/themes';
import styled, {css, ThemeProvider} from 'styled-components';
import ProjectButtons, { ButtonsWrapper } from '../components/ProjectButtons';
import ProjectHeader, { ProjectHeaderRoot } from '../components/ProjectHeader';
import { bProfile, useProfile } from '@brg/auth';
import { executeTypograf } from 'shared/utils/executeTypograf';
import {BTagsGridNew} from "../../../../core/components/bTag/bTagsGridNew/bTagsGridNew";
import { useGetFriendlyUrlParam } from 'shared/hooks/useGetFriendlyUrlParam';

export const DEFAULT_VIEW_TYPE = 'container';

export const ProjectBottomActionsWrapper = styled(FlexBox)<{
  isModer?: boolean,
  viewType?: string,
}>`
  width: 100%;
  flex-flow: column;
  // margin: 24px auto 0;
  margin: 0 auto;
  justify-content: center;
  place-items: center;
  background: #fff;
  padding: 24px 48px;
  //border-radius: 4px;
  max-width: 1152px;

  ${ProjectHeaderRoot} {
    margin-top: 0;
    width: 100%;
    text-align: center;
    * {
      color: rgba(0,0,0,0.47);
    }
    .ProjectHeaderMadeBy {
      color: #151514;
    }

    @media(max-width: ${LayoutConst.screen.s - 1}px) {
      padding-right: 0;
    }
  }

  ${ButtonsWrapper} > div {
    &:not(:last-child) {
        margin-right: 32px;
    }

    svg path {
      fill: rgba(0,0,0,0.47);
    }

    svg:hover path {
      fill: ${Colors.galBlack100};
    }
    .count-icon-wrapper svg:hover path {
      fill: rgba(0,0,0,0.47);
    }
    .project-tools-send svg:hover path {
      fill: ${Colors.galBlack100}!important;
    }
    padding: 0;
    order: 0;
    @media(max-width: ${LayoutConst.screen.sm}px) {
        &:not(:last-child) {
            margin-right: ${p => p?.isModer ? "0" : "12px"};
        }
    }
  }

  .LikesButtonWrapper, .ViewsCountWrapper {
    max-width: fit-content !important;
  }

  .likes-icon-wrapper {
    flex-direction: row !important;
    place-items: center;
    display: flex;

    @media(max-width: ${LayoutConst.screen.sm}px) {
        flex-direction: column !important;
    }
  }

  .likes-icon-wrapper > span {
    margin-left: 8px;
    margin-top: 0;
    font-size: 11px;
    line-height: 13px;
    color: rgba(0,0,0,0.47);

    @media(max-width: ${LayoutConst.screen.sm}px) {
        margin: 0;
    }
  }

  .count-icon-wrapper {
    flex-direction: row !important;
    place-items: center;
    display: flex;
    @media(max-width: ${LayoutConst.screen.sm}px) {
        flex-direction: column !important;
    }
  }

  .count-icon-wrapper span {
    margin-left: 8px;
    margin-top: 2px;
    color: rgba(0,0,0,0.47);
    @media(max-width: ${LayoutConst.screen.sm}px) {
        margin: 0;
    }
  }

  .ps-action-btn {
    padding: 0 !important;
    margin-bottom: 0;
  }
  .ps-action-moder-btn {
    padding: 0;
  }

    @media (max-width: ${LayoutConst.screen.mac13 + 1}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {

    padding: 24px 40px;

      width: 888px;
      }

    @media(max-width: ${LayoutConst.screen.md}px) {
      padding: 16px 32px;
      margin: 0 auto;
      width: calc(100% - 32px);
    }
  
    @media(max-width: ${LayoutConst.screen.s - 1}px) {
      width: 100%;
      padding: 16px;
      margin-top: 8px;
    }
`;


const Title = styled(FInter.MainTitleMediumDiv)<{ hseFonts?: boolean }>`
  font-size: 30px;
  line-height: 40px;
  font-weight: 500;
  letter-spacing: -0.02em;
  color: #151514;
  text-align: center;

  @media (max-width: 834px) {
    font-size: 22px;
    line-height: 28px;
  }

  @media (max-width: ${LayoutConst.screen.sm}px) {
    font-size: 17px;
    line-height: 22px;
  }

  .MoreButtonWrapper {
    align-items: flex-start;
  }
`;


export const PSBottomActions: FC<{ 
  theme: ApplicationThemeT, 
  project: Project, 
  titleVisible: boolean, 
  isEmptyProjectTags?: boolean, 
  isPresentationTooltip?: boolean, 
  isLoading?: boolean, 
  tags: ReactNode;
  tagsCount: number;
  approveDialog: ReactNode;
}> = ({ 
  isLoading, 
  theme, 
  isPresentationTooltip, 
  project, 
  isEmptyProjectTags, 
  titleVisible = true,
  tags,
  tagsCount,
  approveDialog
}) => {
  const id = useGetFriendlyUrlParam("id");
  const projectIdRef = useRef<{ id: string }>(null);
  const [collapseTags, setCollapseTags] = useState(false);
  const isPreview = !!useRouteMatch(['/preview', '/books/book-preview']);
  const profile = useProfile();
  const isMac13 = useBreakpoint(LayoutConst.screen.mac13);
  const is940 = useBreakpoint(LayoutConst.screen.md);
  const is743 = useBreakpoint(LayoutConst.screen.s - 1)
  //@ts-ignore
  const editedTitle = project?.additionalContent?.title?.text
  
  const smHeight = useMemo(() => (window.innerHeight < LayoutConst.screen.height.projectCover), [])
  const viewType = useMemo(() => (project?.longread?.viewType || DEFAULT_VIEW_TYPE), [project])

  const isModer = useMemo(() => profile && (bProfile.isModer(profile) || bProfile.isMiddleModer(profile)), [profile]);

  const maxTagHeight = (isMac13 || smHeight) ? 140 : 162;

  //@ts-ignore
  projectIdRef.current = id;

  useEffect(() => {
    setTimeout(() => {
      //@ts-ignore
      if (projectIdRef.current === id ) {
        const tagsContainer = document.querySelector('#tags_bottom_container');
        if (tagsContainer) {
          //@ts-ignore
          if (tagsContainer.offsetHeight > maxTagHeight) {
            setCollapseTags(true);
          }
        }
      }
    } , 100)
       
  },[ projectIdRef, id, maxTagHeight]);

  if (isPreview) {
    return null;
  }

  if (!project) {
      return (
          <ProjectBottomActionsWrapper viewType={viewType}>
              <Skeleton width={'100%'} height={'154px'} />
          </ProjectBottomActionsWrapper>
      )
  }
    return (
        <ProjectBottomActionsWrapper isModer={isModer} viewType={viewType}>
          {titleVisible && (
            <>
              <Title>{executeTypograf(editedTitle ?? project?.title ?? 'untitled')}</Title>
              <FlexBox offsetTop={8} offsetTopMobile={0} offsetLeftMobile={10} offsetRightMobile={10} widthMobile={'100%'}>
                  <ProjectHeader project={project} variant="bottom"/>
              </FlexBox>
            </>
          )}
          <ThemeProvider theme={ApplicationTheme.white as any}>
            {Boolean(tagsCount) && <BTagsGridNew style={{marginTop: is940 ? '16px' : 0}} id="tags_bottom_container" scrollMode="all" shadow justify="center">
              {tags}
            </BTagsGridNew>}
          </ThemeProvider>
          <FlexBox offsetTop={Boolean(tagsCount) ? 22 : 10} offsetTopMobile={titleVisible ? is743? 24 : 14 : 0}>
            <ProjectButtons dropdownVerticalPos={"top"} project={project} orientation='horizontal' dropdownPos={'right'} isBottomTooltip={true} isPresentationTooltip={isPresentationTooltip} />
          </FlexBox>
          {approveDialog && 
            <FlexBox>
              {approveDialog}
            </FlexBox>
          }
        </ProjectBottomActionsWrapper>
    )
}import { Project } from '@brg/api';
import {Colors, FInter, FlexBox, Skeleton, useBreakpoint} from '@brg/ui';
import { LayoutConst } from 'core';
import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useRouteMatch } from 'react-router-dom';

import {ApplicationTheme, ApplicationThemeT} from 'shared/themes';
import styled, {css, ThemeProvider} from 'styled-components';
import ProjectButtons, { ButtonsWrapper } from '../components/ProjectButtons';
import ProjectHeader, { ProjectHeaderRoot } from '../components/ProjectHeader';
import { bProfile, useProfile } from '@brg/auth';
import { executeTypograf } from 'shared/utils/executeTypograf';
import {BTagsGridNew} from "../../../../core/components/bTag/bTagsGridNew/bTagsGridNew";
import { useGetFriendlyUrlParam } from 'shared/hooks/useGetFriendlyUrlParam';

export const DEFAULT_VIEW_TYPE = 'container';

export const ProjectBottomActionsWrapper = styled(FlexBox)<{
  isModer?: boolean,
  viewType?: string,
}>`
  width: 100%;
  flex-flow: column;
  // margin: 24px auto 0;
  margin: 0 auto;
  justify-content: center;
  place-items: center;
  background: #fff;
  padding: 24px 48px;
  //border-radius: 4px;
  max-width: 1152px;

  ${ProjectHeaderRoot} {
    margin-top: 0;
    width: 100%;
    text-align: center;
    * {
      color: rgba(0,0,0,0.47);
    }
    .ProjectHeaderMadeBy {
      color: #151514;
    }

    @media(max-width: ${LayoutConst.screen.s - 1}px) {
      padding-right: 0;
    }
  }

  ${ButtonsWrapper} > div {
    &:not(:last-child) {
        margin-right: 32px;
    }

    svg path {
      fill: rgba(0,0,0,0.47);
    }

    svg:hover path {
      fill: ${Colors.galBlack100};
    }
    .count-icon-wrapper svg:hover path {
      fill: rgba(0,0,0,0.47);
    }
    .project-tools-send svg:hover path {
      fill: ${Colors.galBlack100}!important;
    }
    padding: 0;
    order: 0;
    @media(max-width: ${LayoutConst.screen.sm}px) {
        &:not(:last-child) {
            margin-right: ${p => p?.isModer ? "0" : "12px"};
        }
    }
  }

  .LikesButtonWrapper, .ViewsCountWrapper {
    max-width: fit-content !important;
  }

  .likes-icon-wrapper {
    flex-direction: row !important;
    place-items: center;
    display: flex;

    @media(max-width: ${LayoutConst.screen.sm}px) {
        flex-direction: column !important;
    }
  }

  .likes-icon-wrapper > span {
    margin-left: 8px;
    margin-top: 0;
    font-size: 11px;
    line-height: 13px;
    color: rgba(0,0,0,0.47);

    @media(max-width: ${LayoutConst.screen.sm}px) {
        margin: 0;
    }
  }

  .count-icon-wrapper {
    flex-direction: row !important;
    place-items: center;
    display: flex;
    @media(max-width: ${LayoutConst.screen.sm}px) {
        flex-direction: column !important;
    }
  }

  .count-icon-wrapper span {
    margin-left: 8px;
    margin-top: 2px;
    color: rgba(0,0,0,0.47);
    @media(max-width: ${LayoutConst.screen.sm}px) {
        margin: 0;
    }
  }

  .ps-action-btn {
    padding: 0 !important;
    margin-bottom: 0;
  }
  .ps-action-moder-btn {
    padding: 0;
  }

    @media (max-width: ${LayoutConst.screen.mac13 + 1}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {

    padding: 24px 40px;

      width: 888px;
      }

    @media(max-width: ${LayoutConst.screen.md}px) {
      padding: 16px 32px;
      margin: 0 auto;
      width: calc(100% - 32px);
    }
  
    @media(max-width: ${LayoutConst.screen.s - 1}px) {
      width: 100%;
      padding: 16px;
      margin-top: 8px;
    }
`;


const Title = styled(FInter.MainTitleMediumDiv)<{ hseFonts?: boolean }>`
  font-size: 30px;
  line-height: 40px;
  font-weight: 500;
  letter-spacing: -0.02em;
  color: #151514;
  text-align: center;

  @media (max-width: 834px) {
    font-size: 22px;
    line-height: 28px;
  }

  @media (max-width: ${LayoutConst.screen.sm}px) {
    font-size: 17px;
    line-height: 22px;
  }

  .MoreButtonWrapper {
    align-items: flex-start;
  }
`;


export const PSBottomActions: FC<{ 
  theme: ApplicationThemeT, 
  project: Project, 
  titleVisible: boolean, 
  isEmptyProjectTags?: boolean, 
  isPresentationTooltip?: boolean, 
  isLoading?: boolean, 
  tags: ReactNode;
  tagsCount: number;
  approveDialog: ReactNode;
}> = ({ 
  isLoading, 
  theme, 
  isPresentationTooltip, 
  project, 
  isEmptyProjectTags, 
  titleVisible = true,
  tags,
  tagsCount,
  approveDialog
}) => {
  const id = useGetFriendlyUrlParam("id");
  const projectIdRef = useRef<{ id: string }>(null);
  const [collapseTags, setCollapseTags] = useState(false);
  const isPreview = !!useRouteMatch(['/preview', '/books/book-preview']);
  const profile = useProfile();
  const isMac13 = useBreakpoint(LayoutConst.screen.mac13);
  const is940 = useBreakpoint(LayoutConst.screen.md);
  const is743 = useBreakpoint(LayoutConst.screen.s - 1)
  //@ts-ignore
  const editedTitle = project?.additionalContent?.title?.text
  
  const smHeight = useMemo(() => (window.innerHeight < LayoutConst.screen.height.projectCover), [])
  const viewType = useMemo(() => (project?.longread?.viewType || DEFAULT_VIEW_TYPE), [project])

  const isModer = useMemo(() => profile && (bProfile.isModer(profile) || bProfile.isMiddleModer(profile)), [profile]);

  const maxTagHeight = (isMac13 || smHeight) ? 140 : 162;

  //@ts-ignore
  projectIdRef.current = id;

  useEffect(() => {
    setTimeout(() => {
      //@ts-ignore
      if (projectIdRef.current === id ) {
        const tagsContainer = document.querySelector('#tags_bottom_container');
        if (tagsContainer) {
          //@ts-ignore
          if (tagsContainer.offsetHeight > maxTagHeight) {
            setCollapseTags(true);
          }
        }
      }
    } , 100)
       
  },[ projectIdRef, id, maxTagHeight]);

  if (isPreview) {
    return null;
  }

  if (!project) {
      return (
          <ProjectBottomActionsWrapper viewType={viewType}>
              <Skeleton width={'100%'} height={'154px'} />
          </ProjectBottomActionsWrapper>
      )
  }
    return (
        <ProjectBottomActionsWrapper isModer={isModer} viewType={viewType}>
          {titleVisible && (
            <>
              <Title>{executeTypograf(editedTitle ?? project?.title ?? 'untitled')}</Title>
              <FlexBox offsetTop={8} offsetTopMobile={0} offsetLeftMobile={10} offsetRightMobile={10} widthMobile={'100%'}>
                  <ProjectHeader project={project} variant="bottom"/>
              </FlexBox>
            </>
          )}
          <ThemeProvider theme={ApplicationTheme.white as any}>
            {Boolean(tagsCount) && <BTagsGridNew style={{marginTop: is940 ? '16px' : 0}} id="tags_bottom_container" scrollMode="all" shadow justify="center">
              {tags}
            </BTagsGridNew>}
          </ThemeProvider>
          <FlexBox offsetTop={Boolean(tagsCount) ? 22 : 10} offsetTopMobile={titleVisible ? is743? 24 : 14 : 0}>
            <ProjectButtons dropdownVerticalPos={"top"} project={project} orientation='horizontal' dropdownPos={'right'} isBottomTooltip={true} isPresentationTooltip={isPresentationTooltip} />
          </FlexBox>
          {approveDialog && 
            <FlexBox>
              {approveDialog}
            </FlexBox>
          }
        </ProjectBottomActionsWrapper>
    )
}